// What the order view's Order / Pack / Label / Decl menus do with a file:
// print it, open it, share it to another app (a printer's app, Files,
// email…) or download it. Every action runs straight from the tap, on a
// file already in hand, so browsers allow the new tab, the print dialog and
// the share sheet.

const TYPES = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  zpl: "text/plain",
  txt: "text/plain",
};

export const typeFromName = (name) =>
  TYPES[
    String(name ?? "")
      .split(".")
      .pop()
      .toLowerCase()
  ] || "application/octet-stream";

// A named file with a type viewers understand (a label often comes as a
// bare blob).
export const asFile = (blob, name) => {
  const type =
    blob?.type && blob.type !== "application/octet-stream"
      ? blob.type
      : typeFromName(name);
  return new File([blob], name, { type });
};

// Phones and tablets.
export const isTouch = () => {
  try {
    return Boolean(window.matchMedia?.("(pointer: coarse)").matches);
  } catch (error) {
    return false;
  }
};

// Whether this browser can hand these files to another app.
export const canShareFiles = (files) => {
  try {
    return Boolean(
      files?.length && navigator.share && navigator.canShare?.({ files }),
    );
  } catch (error) {
    return false;
  }
};

// Safari and Firefox don't print a PDF from a hidden frame reliably (Safari
// prints the page, Firefox refuses): there the file opens to print instead.
const framePrintable = () => {
  const agent = navigator.userAgent || "";
  return (
    !/firefox/i.test(agent) && !/^((?!chrome|android).)*safari/i.test(agent)
  );
};

const urls = new Map();
const urlFor = (file) => {
  if (!urls.has(file)) urls.set(file, URL.createObjectURL(file));
  return urls.get(file);
};

// Frees the files' addresses (a minute on, so a tab still opening keeps it).
export const releaseFiles = (files) =>
  setTimeout(
    () =>
      (files || []).forEach((file) => {
        const url = urls.get(file);
        if (!url) return;
        URL.revokeObjectURL(url);
        urls.delete(file);
      }),
    60000,
  );

const clickLink = (attributes) => {
  const link = document.createElement("a");
  Object.entries(attributes).forEach(([name, value]) =>
    link.setAttribute(name, value),
  );
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const downloadFile = (file) =>
  clickLink({ href: urlFor(file), download: file.name });

export const openFile = (file) =>
  clickLink({ href: urlFor(file), target: "_blank", rel: "noopener" });

// The print dialog for the file (on desktops). Phones, tablets, Safari and
// Firefox open it instead, to print from its viewer.
export const printFile = (file) => {
  if (isTouch() || !framePrintable()) {
    openFile(file);
    return "opened";
  }
  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.setAttribute("title", file.name);
  frame.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden";
  frame.onload = () => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch (error) {
      openFile(file);
    }
    setTimeout(() => frame.remove(), 60000);
  };
  frame.src = urlFor(file);
  document.body.appendChild(frame);
  return "printing";
};

export const shareFiles = (files, title) => navigator.share({ files, title });
